import { useEffect, useState } from "react"
import { Link, useLocation } from "@remix-run/react"
import type { ForumBoardSlug } from "~/utils/forum"
import { cn } from "~/utils/shadecn"

// A year, not a session: either button means "seen", once per browser.
const SEEN_COOKIE = "mlnp_forum_tag_seen"
const hasSeenTag = () => document.cookie.split("; ").some((cookie) => cookie.startsWith(`${SEEN_COOKIE}=`))
const markTagSeen = () => {
  document.cookie = `${SEEN_COOKIE}=1; path=/; max-age=31536000; SameSite=Lax`
}

const sans = "font-['DM_Sans',sans-serif]"

/** The tag's "NEW" pill, also beside the Forum item in the user menu. */
export const NewBadge = ({ className }: { className?: string }) => (
  <span
    className={cn(
      sans,
      "flex-none rounded-full bg-[#A9532F] px-2 py-[3px] text-[11px] font-bold leading-[14px] tracking-[0.1em] text-white",
      className
    )}>
    NEW
  </span>
)

/**
 * The forums launch announcement: a gift tag hanging from the user menu's avatar, on Explore only. Rendered
 * inside the avatar's wrapper, so every offset below is measured from the avatar's bottom-right corner.
 */
export default function ForumAnnouncement({ board }: { board?: ForumBoardSlug }) {
  const [open, setOpen] = useState(false)
  const onExplore = useLocation().pathname === "/videos"

  // Decided after mount so the server render and hydration agree; cookies are client-only here.
  useEffect(() => {
    if (board && !hasSeenTag()) setOpen(true)
  }, [board])

  if (!board || !open || !onExplore) return null

  const dismiss = () => {
    markTagSeen()
    setOpen(false)
  }

  return (
    <>
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=Caveat:wght@600&family=DM+Sans:wght@400;500;600;700&display=swap"
      />
      <span className="pointer-events-none absolute -right-px -top-px h-[11px] w-[11px] rounded-full border-2 border-white bg-[#BF6040]" />

      <div className="pointer-events-none absolute right-0 top-full h-[330px] w-[260px]">
        <svg aria-hidden width="140" height="142" viewBox="0 0 140 142" className="absolute right-0 top-0.5">
          <path
            d="M9 138 C 20 98, 70 112, 84 82 C 96 56, 70 46, 76 68 C 82 90, 118 54, 122 8"
            fill="none"
            stroke="#BF6040"
            strokeWidth="2.5"
            strokeLinecap="round"
          />
          <path d="M114.5 15 L 122 5 L 129.5 15" fill="none" stroke="#BF6040" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>

        <p
          aria-hidden
          className="absolute right-[116px] top-[38px] hidden w-[116px] origin-top-left min-[1400px]:block -rotate-[5deg] text-right font-['Caveat',cursive] text-[23px] font-semibold leading-[22px] text-[#A9532F] dark:text-[#E08A67]">
          find it here from now on
        </p>

        <div className="pointer-events-auto absolute right-[14px] top-[122px] flex w-[236px] origin-top-left -rotate-[4deg] flex-col gap-3.5 px-[22px] pb-[22px] pt-[52px]">
          <svg
            aria-hidden
            viewBox="0 0 236 236"
            preserveAspectRatio="none"
            className="absolute inset-0 h-full w-full drop-shadow-[0_10px_14px_rgba(59,34,22,0.15)]">
            <path
              d="M44 0.5 H192 L235.5 40 V226 Q235.5 235.5 226 235.5 H10 Q0.5 235.5 0.5 226 V40 Z"
              vectorEffect="non-scaling-stroke"
              fill="#FFFFFF"
              stroke="#E6CBBE"
            />
          </svg>
          <span className="absolute left-[109px] top-4 h-[18px] w-[18px] rounded-full border-[2.5px] border-[#E6CBBE] bg-[#F6F5F3]" />

          <NewBadge className="relative self-start" />
          <div className={`${sans} relative flex flex-col gap-1`}>
            <p className="text-[17px] font-bold leading-[22px] tracking-[-0.01em] text-[#211D1C]">The Forum is here 🧡</p>
            <p className="text-[14px] leading-5 text-[#5E5652]">A place to chat, ask, and share about real world sex.</p>
            <p className="pt-0.5 text-[13px] leading-[18px] text-[#776E69]">Connect with other members and MakeLoveNotPornstars.</p>
          </div>
          <div className={`${sans} relative flex items-center gap-3.5 border-t border-dashed border-[#E6CBBE] pt-3.5`}>
            <Link
              to={`/forum/${board}`}
              onClick={dismiss}
              className="flex h-[34px] items-center gap-1.5 rounded-lg bg-[#A9532F] px-3.5 text-[14px] font-semibold leading-[18px] text-white hover:bg-[#954828]">
              Take a look
              <svg aria-hidden width="14" height="14" viewBox="0 0 14 14">
                <path d="M2 7h10M8 3l4 4-4 4" fill="none" stroke="#FFFFFF" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Link>
            <button
              type="button"
              onClick={dismiss}
              className="text-[14px] font-medium leading-[18px] text-[#776E69] underline decoration-1 underline-offset-[3px] hover:text-[#211D1C]">
              Got it
            </button>
          </div>
        </div>

        <svg aria-hidden width="66" height="46" viewBox="0 0 64 44" className="absolute right-[98px] top-[118px]">
          <path d="M32 22 C 22 6, 4 4, 6 18 C 8 30, 24 28, 32 22 Z" fill="#BF6040" />
          <path d="M32 22 C 42 6, 60 4, 58 18 C 56 30, 40 28, 32 22 Z" fill="#BF6040" />
          <path d="M29 24 L 22 42 M35 24 L 42 42" stroke="#A9532F" strokeWidth="5" strokeLinecap="round" />
          <path d="M14 16 C 18 14, 24 16, 28 20" fill="none" stroke="#E08A67" strokeWidth="1.5" strokeLinecap="round" />
          <path d="M50 16 C 46 14, 40 16, 36 20" fill="none" stroke="#E08A67" strokeWidth="1.5" strokeLinecap="round" />
          <circle cx="32" cy="22" r="6" fill="#A9532F" />
        </svg>
      </div>
    </>
  )
}
